import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { HubConnectionState } from '@microsoft/signalr';
import { MessageCircle, ShieldAlert, Pencil, X, ArrowUp } from 'lucide-react';
import { api, getDiscussionConnection, relativeTime } from '@watchparty/shared';
import { useAuth } from '@/features/auth';
import Avatar from '@/components/ui/Avatar';
function groupMessages(messages) {
    const groups = [];
    for (const msg of messages) {
        const last = groups[groups.length - 1];
        if (last && last.userId === msg.userId) {
            last.messages.push(msg);
        }
        else {
            groups.push({
                userId: msg.userId,
                displayName: msg.displayName,
                avatarUrl: msg.avatarUrl,
                avatarColor: msg.avatarColor,
                messages: [msg],
            });
        }
    }
    return groups;
}
// ---------------------------------------------------------------------------
// MessageBubble
// ---------------------------------------------------------------------------
function MessageBubble({ msg, isOwn, position, onEdit, }) {
    const [revealed, setRevealed] = useState(false);
    const [hovered, setHovered] = useState(false);
    if (msg.isDeleted) {
        return (_jsx("div", { style: {
                padding: '2px 0',
                paddingLeft: isOwn ? undefined : '40px',
                fontSize: '0.75rem',
                color: 'var(--color-text-faint)',
                fontStyle: 'italic',
                fontFamily: 'var(--font-family-body)',
                textAlign: isOwn ? 'right' : 'left',
            }, children: "Message deleted" }));
    }
    const showSpoiler = msg.isSpoiler && !revealed;
    // iMessage-style bubble radius based on position in group
    const radius = '18px';
    const tail = '4px';
    let borderRadius;
    if (isOwn) {
        // Right-aligned: tail on bottom-right
        switch (position) {
            case 'solo':
                borderRadius = `${radius} ${radius} ${tail} ${radius}`;
                break;
            case 'first':
                borderRadius = `${radius} ${radius} ${tail} ${radius}`;
                break;
            case 'middle':
                borderRadius = `${radius} ${tail} ${tail} ${radius}`;
                break;
            case 'last':
                borderRadius = `${radius} ${tail} ${radius} ${radius}`;
                break;
        }
    }
    else {
        // Left-aligned: tail on bottom-left
        switch (position) {
            case 'solo':
                borderRadius = `${radius} ${radius} ${radius} ${tail}`;
                break;
            case 'first':
                borderRadius = `${radius} ${radius} ${radius} ${tail}`;
                break;
            case 'middle':
                borderRadius = `${tail} ${radius} ${radius} ${tail}`;
                break;
            case 'last':
                borderRadius = `${tail} ${radius} ${radius} ${radius}`;
                break;
        }
    }
    return (_jsx("div", { style: {
            display: 'flex',
            justifyContent: isOwn ? 'flex-end' : 'flex-start',
            paddingLeft: isOwn ? '48px' : '40px',
            paddingRight: isOwn ? '12px' : '48px',
            marginBottom: '2px',
        }, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), children: _jsxs("div", { style: { position: 'relative', maxWidth: '75%' }, children: [_jsx("div", { style: {
                        padding: '8px 12px',
                        borderRadius,
                        background: isOwn ? 'var(--color-accent)' : 'var(--color-surface-sunken)',
                        color: isOwn ? 'var(--color-text-inverse)' : 'var(--color-text)',
                        fontSize: '0.9rem',
                        fontFamily: 'var(--font-family-body)',
                        lineHeight: 1.35,
                        wordBreak: 'break-word',
                        filter: showSpoiler ? 'blur(8px)' : 'none',
                        overflow: showSpoiler ? 'hidden' : undefined,
                        userSelect: showSpoiler ? 'none' : undefined,
                        cursor: showSpoiler ? 'pointer' : undefined,
                    }, onClick: showSpoiler ? () => setRevealed(true) : undefined, children: msg.content }), showSpoiler && (_jsx("div", { onClick: () => setRevealed(true), style: {
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                    }, children: _jsxs("span", { style: {
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 10px',
                            borderRadius: '999px',
                            background: 'var(--color-warning-subtle)',
                            color: 'var(--color-warning)',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            fontFamily: 'var(--font-family-body)',
                        }, children: [_jsx(ShieldAlert, { size: 12 }), "Spoiler"] }) })), isOwn && hovered && !showSpoiler && (_jsx("button", { onClick: () => onEdit(msg), style: {
                        position: 'absolute',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        left: '-32px',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        border: '1px solid var(--color-border)',
                        background: 'var(--color-surface)',
                        color: 'var(--color-text-muted)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                    }, children: _jsx(Pencil, { size: 11 }) }))] }) }));
}
// ---------------------------------------------------------------------------
// MessageGroupView — renders a cluster of same-sender messages
// ---------------------------------------------------------------------------
function MessageGroupView({ group, isOwn, onEdit, }) {
    const count = group.messages.length;
    const lastMsg = group.messages[count - 1];
    return (_jsxs("div", { style: { marginBottom: '10px' }, children: [!isOwn && (_jsx("div", { style: {
                    paddingLeft: '40px',
                    marginBottom: '2px',
                }, children: _jsx("span", { style: {
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: 'var(--color-text-muted)',
                        fontFamily: 'var(--font-family-body)',
                    }, children: group.displayName }) })), _jsxs("div", { style: {
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-end',
                    gap: 0,
                }, children: [!isOwn && (_jsx("div", { style: { width: '32px', flexShrink: 0, marginRight: '8px' }, children: _jsx(Avatar, { displayName: group.displayName, avatarUrl: group.avatarUrl, avatarColor: group.avatarColor, size: 28 }) })), _jsx("div", { style: { flex: 1, minWidth: 0 }, children: group.messages.map((msg, i) => {
                            const position = count === 1 ? 'solo' :
                                i === 0 ? 'first' :
                                    i === count - 1 ? 'last' :
                                        'middle';
                            return (_jsx(MessageBubble, { msg: msg, isOwn: isOwn, position: position, onEdit: onEdit }, msg.discussionMessageId));
                        }) })] }), _jsx("div", { style: {
                    paddingLeft: isOwn ? undefined : '40px',
                    paddingRight: isOwn ? '0' : undefined,
                    textAlign: isOwn ? 'right' : 'left',
                    marginTop: '2px',
                }, children: _jsxs("span", { style: {
                        fontSize: '0.65rem',
                        color: 'var(--color-text-faint)',
                        fontFamily: 'var(--font-family-body)',
                    }, children: [relativeTime(lastMsg.createdAt), lastMsg.editedAt ? ' (edited)' : ''] }) })] }));
}
// ---------------------------------------------------------------------------
// DiscussionTab
// ---------------------------------------------------------------------------
export function DiscussionTab({ partyId, workId }) {
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const [input, setInput] = useState('');
    const [isSpoiler, setIsSpoiler] = useState(false);
    const [sending, setSending] = useState(false);
    const [liveMessages, setLiveMessages] = useState([]);
    const [editingMessageId, setEditingMessageId] = useState(null);
    const messagesEndRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const containerRef = useRef(null);
    const joinedRef = useRef(null);
    const inputRef = useRef(null);
    const [containerHeight, setContainerHeight] = useState('400px');
    // Measure position and fill remaining viewport height
    useEffect(() => {
        function measure() {
            const el = containerRef.current;
            if (!el)
                return;
            const top = el.getBoundingClientRect().top;
            const available = window.innerHeight - top - 16;
            setContainerHeight(`${Math.max(300, available)}px`);
        }
        // Use rAF to measure after the browser has laid out the content
        const frame = requestAnimationFrame(measure);
        window.addEventListener('resize', measure);
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('resize', measure);
        };
    });
    // Fetch the discussion (get-or-create) — party chat or work-level
    const discussionQueryKey = workId
        ? ['party', partyId, 'work', workId, 'discussion']
        : ['party', partyId, 'discussion'];
    const discussionEndpoint = workId
        ? `/parties/${partyId}/works/${workId}/discussion`
        : `/parties/${partyId}/discussion`;
    const { data: discussion, isLoading: discussionLoading } = useQuery({
        queryKey: discussionQueryKey,
        queryFn: () => api.get(discussionEndpoint),
    });
    const discussionId = discussion?.discussionId;
    // Fetch existing messages
    const { data: messagesData, isLoading: messagesLoading } = useQuery({
        queryKey: ['discussion', discussionId, 'messages'],
        queryFn: () => api.get(`/discussions/${discussionId}/messages?pageSize=100`),
        enabled: !!discussionId,
    });
    const historicMessages = messagesData?.items ?? [];
    // Merge historic + live, dedup by id, prefer live version for edits
    const allMessages = (() => {
        const liveMap = new Map(liveMessages.map(m => [m.discussionMessageId, m]));
        const merged = historicMessages.map(m => liveMap.get(m.discussionMessageId) ?? m);
        const seen = new Set(merged.map(m => m.discussionMessageId));
        for (const m of liveMessages) {
            if (!seen.has(m.discussionMessageId)) {
                merged.push(m);
                seen.add(m.discussionMessageId);
            }
        }
        return merged;
    })();
    const messageGroups = groupMessages(allMessages);
    // Auto-scroll to bottom on new messages
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            container.scrollTop = container.scrollHeight;
        }
    }, [allMessages.length]);
    // SignalR connection management
    useEffect(() => {
        if (!discussionId)
            return;
        const hub = getDiscussionConnection();
        function handleReceiveMessage(msg) {
            if (msg.userId === user?.userId)
                return;
            setLiveMessages(prev => [...prev, msg]);
        }
        function handleMessageEdited(msg) {
            setLiveMessages(prev => {
                const exists = prev.some(m => m.discussionMessageId === msg.discussionMessageId);
                if (exists) {
                    return prev.map(m => m.discussionMessageId === msg.discussionMessageId ? msg : m);
                }
                return [...prev, msg];
            });
            queryClient.invalidateQueries({ queryKey: ['discussion', discussionId, 'messages'] });
        }
        async function connect() {
            try {
                if (hub.state === HubConnectionState.Disconnected) {
                    await hub.start();
                }
                await hub.invoke('JoinDiscussion', discussionId);
                joinedRef.current = discussionId;
                hub.on('ReceiveMessage', handleReceiveMessage);
                hub.on('MessageEdited', handleMessageEdited);
            }
            catch (err) {
                console.error('SignalR connection failed:', err);
            }
        }
        connect();
        return () => {
            hub.off('ReceiveMessage', handleReceiveMessage);
            hub.off('MessageEdited', handleMessageEdited);
            if (joinedRef.current) {
                hub.invoke('LeaveDiscussion', joinedRef.current).catch(() => { });
                joinedRef.current = null;
            }
        };
    }, [discussionId, user?.userId, queryClient]);
    // Clear live messages when discussion changes
    useEffect(() => {
        setLiveMessages([]);
    }, [discussionId]);
    const startEdit = useCallback((msg) => {
        setEditingMessageId(msg.discussionMessageId);
        setInput(msg.content ?? '');
        setIsSpoiler(msg.isSpoiler);
        inputRef.current?.focus();
    }, []);
    const cancelEdit = useCallback(() => {
        setEditingMessageId(null);
        setInput('');
        setIsSpoiler(false);
    }, []);
    const handleSend = useCallback(async () => {
        if (!input.trim() || sending)
            return;
        const content = input.trim();
        const spoiler = isSpoiler;
        if (editingMessageId) {
            setSending(true);
            setInput('');
            try {
                const hub = getDiscussionConnection();
                await hub.invoke('EditMessage', editingMessageId, content, spoiler);
            }
            catch (err) {
                console.error('Failed to edit message:', err);
                setInput(content);
            }
            finally {
                setSending(false);
                setEditingMessageId(null);
                setIsSpoiler(false);
            }
            return;
        }
        if (!discussionId)
            return;
        setInput('');
        setIsSpoiler(false);
        setSending(true);
        const optimistic = {
            discussionMessageId: crypto.randomUUID(),
            discussionId: discussionId,
            userId: user?.userId ?? '',
            displayName: user?.displayName ?? 'You',
            avatarUrl: user?.avatarUrl ?? null,
            avatarColor: user?.avatarColor ?? null,
            content,
            isSpoiler: spoiler,
            isDeleted: false,
            editedAt: null,
            deletedAt: null,
            createdAt: new Date().toISOString(),
        };
        setLiveMessages(prev => [...prev, optimistic]);
        try {
            const hub = getDiscussionConnection();
            await hub.invoke('SendMessage', discussionId, content, spoiler);
        }
        catch (err) {
            console.error('Failed to send message:', err);
            setLiveMessages(prev => prev.filter(m => m.discussionMessageId !== optimistic.discussionMessageId));
            setInput(content);
            setIsSpoiler(spoiler);
        }
        finally {
            setSending(false);
        }
    }, [input, isSpoiler, discussionId, sending, user, editingMessageId]);
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
        if (e.key === 'Escape' && editingMessageId) {
            cancelEdit();
        }
    }, [handleSend, editingMessageId, cancelEdit]);
    const isLoading = discussionLoading || (!!discussionId && messagesLoading);
    const hasInput = input.trim().length > 0;
    return (_jsx("div", { ref: containerRef, style: {
            display: 'flex',
            flexDirection: 'column',
            height: containerHeight,
        }, children: isLoading ? (_jsxs("div", { style: { display: 'flex', justifyContent: 'center', padding: '64px 0' }, children: [_jsx("style", { children: `@keyframes spin { to { transform: rotate(360deg) } }` }), _jsx("div", { style: {
                        width: '24px',
                        height: '24px',
                        border: '2px solid var(--color-border)',
                        borderTopColor: 'var(--color-text)',
                        borderRadius: '50%',
                        animation: 'spin 0.6s linear infinite',
                    } })] })) : (_jsxs(_Fragment, { children: [_jsx("div", { ref: scrollContainerRef, style: {
                        flex: 1,
                        overflowY: 'auto',
                        padding: '16px 8px',
                    }, children: _jsxs("div", { style: { display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', minHeight: '100%' }, children: [allMessages.length === 0 ? (_jsxs("div", { style: { textAlign: 'center', padding: '48px 0' }, children: [_jsx(MessageCircle, { size: 36, style: { color: 'var(--color-text-faint)', marginBottom: '12px', opacity: 0.4 } }), _jsx("p", { style: {
                                            fontFamily: 'var(--font-family-body)',
                                            fontSize: '0.875rem',
                                            color: 'var(--color-text-faint)',
                                        }, children: "No messages yet. Start the conversation!" })] })) : (messageGroups.map((group, i) => (_jsx(MessageGroupView, { group: group, isOwn: group.userId === user?.userId, onEdit: startEdit }, `${group.userId}-${i}`)))), _jsx("div", { ref: messagesEndRef })] }) }), editingMessageId && (_jsxs("div", { style: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '6px 12px',
                        background: 'var(--color-surface-sunken)',
                        borderRadius: '12px 12px 0 0',
                        fontSize: '0.75rem',
                        fontFamily: 'var(--font-family-body)',
                        color: 'var(--color-text-muted)',
                    }, children: [_jsxs("span", { style: { display: 'flex', alignItems: 'center', gap: '4px' }, children: [_jsx(Pencil, { size: 12 }), " Editing message"] }), _jsx("button", { onClick: cancelEdit, style: {
                                background: 'none',
                                border: 'none',
                                color: 'var(--color-text-muted)',
                                cursor: 'pointer',
                                padding: '2px',
                                display: 'flex',
                                alignItems: 'center',
                            }, children: _jsx(X, { size: 14 }) })] })), _jsxs("div", { style: {
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 4px',
                    }, children: [_jsx("button", { onClick: () => setIsSpoiler(s => !s), title: isSpoiler ? 'Spoiler enabled' : 'Mark as spoiler', style: {
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                border: 'none',
                                background: isSpoiler ? 'var(--color-warning-subtle)' : 'transparent',
                                color: isSpoiler ? 'var(--color-warning)' : 'var(--color-text-faint)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                transition: 'background 0.15s, color 0.15s',
                            }, children: _jsx(ShieldAlert, { size: 18 }) }), _jsxs("div", { style: {
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                borderRadius: '20px',
                                border: '1px solid var(--color-border)',
                                background: 'var(--color-surface-sunken)',
                                padding: '4px 4px 4px 14px',
                            }, children: [_jsx("input", { ref: inputRef, value: input, onChange: e => setInput(e.target.value), onKeyDown: handleKeyDown, placeholder: editingMessageId ? 'Edit message...' : 'Type a message...', style: {
                                        flex: 1,
                                        padding: '6px 0',
                                        border: 'none',
                                        background: 'transparent',
                                        color: 'var(--color-text)',
                                        fontSize: '0.9rem',
                                        fontFamily: 'var(--font-family-body)',
                                        outline: 'none',
                                    } }), _jsx("button", { onClick: handleSend, disabled: !hasInput || sending, style: {
                                        width: '30px',
                                        height: '30px',
                                        borderRadius: '50%',
                                        border: 'none',
                                        background: hasInput ? 'var(--color-accent)' : 'transparent',
                                        color: hasInput ? 'var(--color-text-inverse)' : 'var(--color-text-faint)',
                                        cursor: hasInput ? 'pointer' : 'default',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        transition: 'background 200ms, color 200ms, box-shadow 200ms',
                                        boxShadow: hasInput ? '0 0 12px var(--color-accent-glow)' : 'none',
                                    }, children: _jsx(ArrowUp, { size: 18, strokeWidth: 2.5 }) })] })] })] })) }));
}
